// The device store (IndexedDB). Everything the rep captures lives here first: projects,
// photo files, the outbox of revisions waiting to be sent, and the signed-in session. The app
// works with no network; the outbox sends when it comes back.

const DB_NAME = "artisan-tablet";
const DB_VERSION = 1;

export type StoreName = "projects" | "blobs" | "outbox" | "kv";

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("projects")) db.createObjectStore("projects", { keyPath: "id" });
      if (!db.objectStoreNames.contains("blobs")) db.createObjectStore("blobs");
      if (!db.objectStoreNames.contains("outbox")) {
        const s = db.createObjectStore("outbox", { keyPath: "revision_id" });
        s.createIndex("by_created", "created_at");
      }
      if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv");
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgraded the schema: close so it can proceed, reopen on next use.
      db.onversionchange = () => {
        db.close();
        opening = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onblocked = () => reject(new Error("indexedDB blocked by another tab"));
  });
  return opening;
}

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  const t = db.transaction(store, mode);
  const result = done(fn(t.objectStore(store)));
  await new Promise<void>((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error ?? new Error("indexedDB transaction failed"));
    t.onabort = () => reject(t.error ?? new Error("indexedDB transaction aborted"));
  });
  return result;
}

export const db = {
  get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
    return tx(store, "readonly", (s) => s.get(key) as IDBRequest<T | undefined>);
  },
  all<T>(store: StoreName): Promise<T[]> {
    return tx(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>);
  },
  put(store: StoreName, value: unknown, key?: IDBValidKey): Promise<IDBValidKey> {
    return tx(store, "readwrite", (s) => (key === undefined ? s.put(value) : s.put(value, key)));
  },
  delete(store: StoreName, key: IDBValidKey): Promise<undefined> {
    return tx(store, "readwrite", (s) => s.delete(key));
  },
  /** For tests and sign-out: forget everything on this device. */
  async clear(store: StoreName): Promise<void> {
    await tx(store, "readwrite", (s) => s.clear());
  },
};

/** Ask the browser not to evict the store under storage pressure (iPadOS honours it for installed apps). */
export async function persistStorage(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
